<template>
  <div>
    <section-page
      title="Webhooks"
      full-height
    >
      <template v-slot:right>
        <filter-button-add @click="$refs.createWebhookModal.open()">
          Webhook
        </filter-button-add>
      </template>

      <div class="columns">
        <div class="column is-4">
          <app-instruction number="1">Each webhook will only show the latest three submissions</app-instruction>
          <app-instruction number="2">If multiple forms are used for a webhook, you must enable 'is multi form' and link them with a unifier field e.g. 'id'</app-instruction>
          <app-instruction number="3">Before activating a webhook, you should link the field maps with a test submission</app-instruction>
          <app-instruction number="4">After linking the field maps and activating the webhook; you should send another test submission to see if a lead is created/updated correctly and if data is being filled out to the expected fields</app-instruction>
        </div>

        <div class="column is-8">
          <app-loading-section
            :loading="isLoading"
            :count="webhooks.length"
            heading="You don't have any webhooks."
          >
            <b-table
              class="webhook_table"
              :data="webhooks"
              :detailed="true"
              :show-detail-icon="false"
              :opened-detailed="webhookDetailsOpened"
              detail-key="id"
            >
              <b-table-column label="Name" field="label" sortable v-slot="props">
                {{ props.row.label }}
              </b-table-column>

              <b-table-column label="Type" field="data_type" sortable v-slot="props">
                {{ getDataTypeLabel(props.row.data_type) }}
              </b-table-column>

              <b-table-column label="Status" field="is_active" sortable v-slot="props">
                {{ props.row.is_active ? 'Active' : 'Inactive' }}
              </b-table-column>

              <b-table-column label="Campaign" field="campaign_id" sortable v-slot="props">
                {{ (props.row.data_type === ENUMS.WebhookDataTypeEnum.Lead.value) ? getCampaignLabel(props.row.campaign_id) : '--' }}
              </b-table-column>

              <b-table-column label="Default Temp" field="temperature" sortable v-slot="props">
                {{ (props.row.data_type === ENUMS.WebhookDataTypeEnum.Lead.value) ? getTemperatureLabel(props.row.temperature) : '--' }}
              </b-table-column>

              <b-table-column cell-class="action_cell" width="120" v-slot="props">
                <button-link @click="manageWebhook(props.row)">
                  Manage
                </button-link>
              </b-table-column>

              <template #detail="props">
                <div class="flex flex-between">
                  <p><small>{{ props.row.webhook_url }}</small></p>
                  <button-link @click="copyURL(props.row.webhook_url)">Copy URL</button-link>
                </div>
              </template>
            </b-table>
          </app-loading-section>
        </div>
      </div>
    </section-page>

    <modal-container>
      <modal-crud-form
        ref="createWebhookModal"
        title="Webhook"
        form-component="webhook-form"
      >
      </modal-crud-form>

      <modal-form-edit
        ref="updateWebhookModal"
        :title="editWebhookTitle"
        form-component="webhook-form"
        @updated="handleUpdate"
      >
        <template v-slot:underForm v-if="this.webhook.data_type === ENUMS.WebhookDataTypeEnum.Lead.value">
          <hr>
          <div class="switch_instruction">
            <h4 class="switch_instruction--heading">Delete Spam Leads</h4>
            <p class="switch_instruction--text">This will delete all leads created by this webhook and that are still in
              the status of new.</p>
            <button-outlined
              @click="deleteSpamRecords"
              :loading="isDeletingSpam"
              :disabled="isDeletingSpam"
            >Delete Spam Leads
            </button-outlined>
          </div>
        </template>

        <!--        Webhook URL-->
        <section-box class="mt-100">
          <form-field-instructions
            heading="Webhook URL"
            text="Send POST requests to this URL in order to send leads directly into Forefront."
          ></form-field-instructions>

          <hr>

          <div class="flex flex-between my-100">
            <p>{{ webhook.webhook_url }}</p>
            <icon-default
              class="copy_icon"
              icon="copy"
              @click="copyURL(webhook.webhook_url)"
              size="is-small"
            ></icon-default>
          </div>
        </section-box>

        <!--        Submissions-->
        <section-page
          :title="submissions.length ? 'Choose Submission' : 'Instructions'"
          :description="submissions.length ? 'Please select a submission below so we can get the field names from the submission that will be used the field mapping below.' : ''"
          is-short
        >
          <!-- First Submission -->
          <div v-if="!submissions.length">
            <ol class="submission_instruction_list">
              <li>Make your first submission to the Webhook URL above</li>
              <li>We'll process the submission and pull out the fields</li>
              <li>You will then be able to map those fields with Forefront fields</li>
              <li>Remember to activate the webhook in the form to the left so that leads will be created from future
                submissions.
              </li>
            </ol>
          </div>

          <template v-else>
            <b-table
              v-if="webhook.is_multi_step_form"
              class="sub-table"
              :data="submissions"
              :columns="columns"
              :checked-rows.sync="checkedRows"
              @check="checkSubmission"
              checkable
              hoverable
            ></b-table>

            <b-table
              v-else
              class="sub-table"
              :data="submissions"
              :columns="columns"
              :selected="selectedSubmission"
              @select="selectSubmission($event)"
              hoverable
            ></b-table>
          </template>
        </section-page>

        <!--        Field Mapping-->
        <section-page
          v-show="submissions.length"
          title="Field Mapping"
          description="Choose how you want incoming data to saved on the lead that is created when a webhook submission is received."
          style="min-height: auto"
        >
          <p
            v-if="Object.keys(selectedSubmission).length === 0"
            class="mb-4 submission_warning"
          >Click on a submission above in order to have options in the fields below.</p>

          <section-collapseable
            v-for="(fields, index) in fieldMaps"
            :key="index"
            :title="`${index} FIELDS`"
            collapseable
            default-expanded
          >
            <template v-slot:expanded>
              <webhook-field-mapping
                v-for="(field, fieldIndex) in fields"
                :key="fieldIndex"
                :field-map="field"
                :field-maps-data="fieldMapsData"
                :submission="filteredSelectedSubmission"
                :connection="getFieldMapConnection(field)"
                @update="updateFieldMapsData"
              ></webhook-field-mapping>
            </template>
          </section-collapseable>

          <div class="flex flex-end mb-50">
            <button-default @click="saveFieldMaps" :loading="isSavingFieldMaps">
              Save
            </button-default>
          </div>
        </section-page>
      </modal-form-edit>
    </modal-container>
  </div>
</template>

<script>
import {subscribeToChannel, unsubscribeToChannel} from '../../services/pusher';
import {ENUMS} from "../../services/enums";
// import WebhookFieldMap from "./components/WebhookFieldMap";
import WebhookFieldMapping from "./components/WebhookFieldMapping";
import dates from "../../services/dates";

export default {
  name: "WebhooksTab",
  components: {
    // WebhookFieldMap,
    WebhookFieldMapping
  },
  data() {
    return {
      isLoading: true,
      webhook: {},
      selectedSubmission: {},
      checkedRows: [],
      fieldMapsData: {},
      fieldMaps: [],
      leadFieldMaps: [],
      contactFieldMaps: [],
      potentialUnifiers: {},
      optionsMap: {},
      isSavingFieldMaps: false,
      isDeletingSpam: false,
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.$store.state.isLoggedIn) {
      unsubscribeToChannel('webhook', this.$store.getters.account.id);
    }
    next();
  },
  created() {
    this.getFieldMaps();
    this.$api.webhooks.all().then((res) => {
      this.$store.commit('setWebhooks', res.data);
      subscribeToChannel('webhook', this.$store.getters.account.id);

      this.isLoading = false;
    });
  },
  computed: {
    webhooks() {
      return this.$store.state.webhooks;
    },
    editWebhookTitle() {
      if (!this.webhook) return 'Webhook';
      if (!this.webhook.data_type) return 'Webhook';
      if (this.webhook.data_type === ENUMS.WebhookDataTypeEnum.Lead.value) return 'Lead Webhook';
      if (this.webhook.data_type === ENUMS.WebhookDataTypeEnum.Contact.value) return 'Contact Webhook';
      return 'Webhook';
    },
    webhookDetailsOpened() {
      return this.$store.state.webhooks.map(webhook => {
        return webhook.id
      });
    },
    submissions() {
      if (this.webhook.submissions) {
        return this.webhook.submissions.map(sub => {
          sub.request_payload._$fp_created_at = dates.formatAsDateTime(sub.created_at);
          return sub.request_payload;
        });
      }

      return [];
    },
    filteredSelectedSubmission() {
      if (this.selectedSubmission) {
        const subCopy = JSON.parse(JSON.stringify(this.selectedSubmission));
        delete subCopy._$fp_created_at;
        return subCopy;
      }
    },
    columns() {
      const placeholder = [
        {
          field: 'key_1',
          label: 'Key 1'
        },
        {
          field: 'key_2',
          label: 'Key 2'
        },
        {
          field: 'created_at',
          label: 'Submitted On'
        },
      ]

      if (this.webhook.submissions && this.webhook.submissions.length) {
        const payload = Object.keys(this.webhook.submissions[0].request_payload)
        return [
          {
            field: payload[0] || '',
            label: payload[0] || '',
          },
          {
            field: payload[1] || '',
            label: payload[1] || ''
          },
          {
            field: '_$fp_created_at',
            label: 'Submitted On'
          },
        ];
      }

      return placeholder;
    },
    status() {
      return this.$store.getters["pipeline/statuses"].find(status => status.id === this.webhook.status_id);
    },
  },
  methods: {
    getCampaignLabel(campaignId) {
      const campaign = this.$store.getters.campaigns.find(c => c.id === campaignId);
      return (campaign) ? campaign.label : 'None'
    },
    getDataTypeLabel(dataType) {
      return this.$utils.newGetEnumLabel(dataType, ENUMS.WebhookDataTypeEnum)
    },
    getTemperatureLabel(temp) {
      return this.$utils.newGetEnumLabel(temp, ENUMS.TemperatureEnum)
    },
    copyURL(url) {
      this.$utils.copyToClipboard(url);
    },
    handleUpdate(event) {
      this.manageWebhook(event)
    },
    manageWebhook(webhook) {
      this.webhook = webhook;
      this.selectedSubmission = {};
      this.fieldMapsData = {};
      this.fieldMaps = []

      if (this.webhook.field_maps || this.webhook.field_maps !== null) {
        this.fieldMapsData = this.$utils.clone(this.webhook.field_maps);
      }

      // console.log('Field Maps Before: ', this.fieldMaps);
      this.fieldMaps = this.generateFieldMaps(this.fieldMapTypes[this.webhook.data_type])
      // console.log('Field Maps After: ', this.fieldMaps);

      this.$refs.updateWebhookModal.open(webhook);
    },
    checkSubmission(checkedSubs) {
      let bindedSub = {};
      let potentialUnifiers = {};

      for (let sub of checkedSubs) {
        for (let prop in sub) {
          if (bindedSub[prop]) {
            potentialUnifiers[prop] = sub[prop];
          }
          bindedSub[prop] = sub[prop];
        }
      }

      delete potentialUnifiers._$fp_created_at;
      this.potentialUnifiers = potentialUnifiers;

      this.selectedSubmission = bindedSub;
    },
    selectSubmission(sub) {
      this.selectedSubmission = sub;
    },
    getFieldMaps() {
      if (this.fieldMaps.length) return;

      this.$api.fieldMaps.get().then(res => {
        this.fieldMapTypes = res.data;
        this.isLoading = false;
      });
    },
    generateFieldMaps(data) {
      let fields = data

      for (let parentProp in data) {
        for (let prop in data[parentProp]) {
          fields[parentProp][prop] = {
            label: prop,
            value: (data[parentProp][prop].value) ? data[parentProp][prop].value : data[parentProp][prop],
            helpText: this.getFieldMapHelpText(data[parentProp][prop])
          };
        }
      }

      return fields;
    },
    getFieldMapHelpText(fieldMap) {
      switch (fieldMap.validation_type) {
        case 'string':
          return 'Maximum character is 255'
        case 'drop_down':
          return `Acceptable values: ${fieldMap.validation_options.toString()}`
        case 'boolean':
          return 'Acceptable values: yes, no, true, false, 1, 0'
        case 'number':
          return 'Expects a number'
        case 'date':
          return 'Expects a date'
      }
    },
    updateFieldMapsData(data) {
      if (data.key === '') return
      this.$set(this.fieldMapsData, data.key, data.info);
    },
    getFieldMapConnection(fieldMap) {
      if (this.webhook.field_maps) {
        for (const [key, item] of Object.entries(this.fieldMapsData)) {
          if (item.field_id === fieldMap.value.field_id && item.type === fieldMap.value.type) {
            return key;
          }
        }
      }

      return '';
    },
    saveFieldMaps() {
      this.isSavingFieldMaps = true
      this.$api.webhooks.updateFieldMaps(this.webhook.id, {'field_maps': this.fieldMapsData})
        .then(() => {
          this.$buefy.toast.open({
            message: 'Webhook field maps was updated',
            type: 'is-success'
          });
        })
        .finally(() => this.isSavingFieldMaps = false)
    },
    deleteSpamRecords() {
      const vm = this;
      this.isDeletingSpam = true;

      this.$buefy.dialog.confirm({
        title: 'Delete Spam Records',
        message: `This will <strong>permanently delete leads</strong> created by <strong>${this.webhook.label}</strong> and still in the <strong>${this.status.label} Status</strong>.<br><br><strong>We can not retrieve them</strong>.<br><br>Please make sure all legitimate leads are taken out of the New status before hitting this button.`,
        confirmText: 'Delete Spam Records',
        type: 'is-danger',
        hasIcon: true,
        onConfirm() {
          this.$api.records.deleteSpamRecords({
            'spam_source': vm.webhook,
            'status_id': vm.webhook.status_id,
          }).then(() => {
            this.$buefy.toast.open({message: "All spam records have been deleted", type: 'is-success'});
          }).finally(() => {
            vm.isDeletingSpam = false;
          });
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

::v-deep {
  .webhook_table {
    table td {
      border-bottom-width: 0!important;
    }
    .detail {
      box-shadow: none !important;
      background: none !important;
    }
    .detail td {
      padding: 0 !important;
      border-bottom-width: 1px!important;
    }
    .detail-container {
      padding: 0 0.75rem 1rem 1rem!important;
    }
  }
}

.copy_icon {
  margin-left: .5rem;
  color: $secondary;
  cursor: pointer;

  &:hover {
    color: $primary;
  }
}

.submission_instruction_list {
  padding-left: 2em;
  li {
    line-height: 1.3;
    margin-bottom: 10px;
  }
}
.submission_warning {
  color: $red;
}


.switch_instruction {
  margin-bottom: 1rem;

  &--heading {
    margin-bottom: 6px;
    font-weight: bold;
  }

  &--text {
    margin-bottom: 8px;
    line-height: 1.2;
  }
}

.lead_section_box {
  margin-bottom: 1.5em !important;
}

</style>
